import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ProductRecommendation } from '@/shared/hooks/api/product'
import type {
  GetLlmChatSessionResponse,
  LlmChatMessageDto,
  LlmChatMessageFeedbackDto,
  LlmChatSessionDto,
  SubmitLlmChatMessageFeedbackPayload,
} from '@/shared/hooks/api/llmChat'
import { submitLlmChatMessageFeedbackApi } from '@/shared/hooks/api/llmChat'

export type SessionLoadStatus = 'idle' | 'loading' | 'loaded' | 'error'

export type SessionState = {
  session: LlmChatSessionDto | null
  messages: LlmChatMessageDto[]
  loadStatus: SessionLoadStatus
  loadError?: string
  serverSyncStamp: number
}

export type StreamingStatus = 'idle' | 'submitted' | 'streaming' | 'error' | 'ready'

export type StreamingState = {
  status: StreamingStatus
  recommendationGatePending?: boolean
  error?: {
    message: string
    retryable: boolean
    userMessageId: string | null
  }
}

export type FeedbackEntry = {
  feedback: LlmChatMessageFeedbackDto | null
  pending: boolean
  optimistic?: LlmChatMessageFeedbackDto | null
}

export type BootstrapEntry = {
  message?: string
  model?: string
}

export type ProductRecommendationsKey = `${string}:${string}`

function productRecommendationsKey(
  productId: string,
  sessionId: string
): ProductRecommendationsKey {
  return `${productId}:${sessionId}`
}

function feedbackFromMessage(
  message: LlmChatMessageDto
): LlmChatMessageFeedbackDto | null {
  if (message.feedbackRating === null) {
    return null
  }
  return {
    rating: message.feedbackRating,
    ...(message.feedbackReason !== null && { reason: message.feedbackReason }),
    ...(message.feedbackOtherComment !== null && {
      otherComment: message.feedbackOtherComment,
    }),
    createdAt: message.feedbackCreatedAt ?? message.createdAt,
    updatedAt: message.feedbackUpdatedAt ?? message.updatedAt,
    submittedByUserId: message.feedbackSubmittedByUserId ?? '',
  }
}

function feedbackMapFromMessages(
  messages: LlmChatMessageDto[]
): Map<string, FeedbackEntry> {
  const map = new Map<string, FeedbackEntry>()
  for (const message of messages) {
    const feedback = feedbackFromMessage(message)
    if (feedback !== null) {
      map.set(message.id, { feedback, pending: false })
    }
  }
  return map
}

function mergeFeedbackMaps(
  existing: Map<string, FeedbackEntry>,
  fromMessages: Map<string, FeedbackEntry>
): Map<string, FeedbackEntry> {
  const next = new Map(existing)
  for (const [messageId, entry] of fromMessages) {
    const current = next.get(messageId)
    if (current?.pending) {
      continue
    }
    next.set(messageId, entry)
  }
  return next
}

function cloneSessionState(state: SessionState): SessionState {
  return {
    ...state,
    messages: [...state.messages],
  }
}

function cloneMap<Key, Value>(map: Map<Key, Value>): Map<Key, Value> {
  return new Map(map)
}

export type LlmChatStoreState = {
  sessions: Map<string, SessionState>
  activeSessionId: string | null
  streaming: Map<string, StreamingState>
  feedback: Map<string, FeedbackEntry>
  bootstrap: Map<string, BootstrapEntry>
  landingModel: string | undefined
  sessionModels: Map<string, string | undefined>
  productRecommendations: Map<ProductRecommendationsKey, ProductRecommendation[]>
}

export type LlmChatStoreActions = {
  setActiveSessionId: (sessionId: string | null) => void
  setSessionLoading: (sessionId: string) => void
  setSessionLoadError: (sessionId: string, error: string) => void
  initializeSessionFromServer: (
    sessionId: string,
    data: GetLlmChatSessionResponse
  ) => void
  setSessionFromSync: (
    sessionId: string,
    data: Pick<GetLlmChatSessionResponse, 'session' | 'messages'>
  ) => void
  setSessionMessages: (sessionId: string, messages: LlmChatMessageDto[]) => void
  upsertSessionMetadata: (session: LlmChatSessionDto) => void
  setStreamingStatus: (sessionId: string, status: StreamingStatus) => void
  handleStreamError: (
    sessionId: string,
    error: {
      message: string
      retryable: boolean
      userMessageId: string | null
    }
  ) => void
  clearStreamError: (sessionId: string) => void
  setRecommendationGatePending: (sessionId: string, pending: boolean) => void
  clearRecommendationGatePending: (sessionId: string) => void
  setFeedbackOptimistic: (
    messageId: string,
    feedback: LlmChatMessageFeedbackDto
  ) => void
  setFeedbackPending: (messageId: string, pending: boolean) => void
  confirmFeedback: (
    messageId: string,
    feedback: LlmChatMessageFeedbackDto
  ) => void
  rollbackFeedback: (messageId: string) => void
  patchMessageFeedback: (
    sessionId: string,
    messageId: string,
    feedback: LlmChatMessageFeedbackDto
  ) => void
  setBootstrapMessage: (sessionId: string, message: string) => void
  setBootstrapModel: (sessionId: string, model: string) => void
  getAndClearBootstrapMessage: (sessionId: string) => string | null
  getAndClearBootstrapModel: (sessionId: string) => string | null
  setLandingModel: (model: string | undefined) => void
  getLandingModel: () => string | undefined
  setSessionModel: (sessionId: string, model: string | undefined) => void
  getSessionModel: (sessionId: string) => string | undefined
  setProductRecommendations: (
    productId: string,
    sessionId: string,
    recommendations: ProductRecommendation[]
  ) => void
  getProductRecommendations: (
    productId: string,
    sessionId: string
  ) => ProductRecommendation[] | undefined
}

export type LlmChatStore = LlmChatStoreState & LlmChatStoreActions

const defaultStreamingState: StreamingState = { status: 'idle' }

export const useLlmChatStore = create<LlmChatStore>()(
  persist(
    (set, get) => ({
      sessions: new Map(),
      activeSessionId: null,
      streaming: new Map(),
      feedback: new Map(),
      bootstrap: new Map(),
      landingModel: undefined,
      sessionModels: new Map(),
      productRecommendations: new Map(),

      setActiveSessionId: (sessionId) => {
        set({ activeSessionId: sessionId })
      },

      setSessionLoading: (sessionId) => {
        set((state) => {
          const sessions = cloneMap(state.sessions)
          const existing = sessions.get(sessionId)
          sessions.set(sessionId, {
            session: existing?.session ?? null,
            messages: existing?.messages ?? [],
            loadStatus: 'loading',
            serverSyncStamp: existing?.serverSyncStamp ?? 0,
          })
          return { sessions }
        })
      },

      setSessionLoadError: (sessionId, error) => {
        set((state) => {
          const sessions = cloneMap(state.sessions)
          const existing = sessions.get(sessionId)
          sessions.set(sessionId, {
            session: existing?.session ?? null,
            messages: existing?.messages ?? [],
            loadStatus: 'error',
            loadError: error,
            serverSyncStamp: existing?.serverSyncStamp ?? 0,
          })
          return { sessions }
        })
      },

      initializeSessionFromServer: (sessionId, data) => {
        set((state) => {
          const sessions = cloneMap(state.sessions)
          sessions.set(sessionId, {
            session: data.session,
            messages: [...data.messages],
            loadStatus: 'loaded',
            serverSyncStamp: Date.now(),
          })
          const fromMessages = feedbackMapFromMessages(data.messages)
          return {
            sessions,
            feedback: mergeFeedbackMaps(state.feedback, fromMessages),
          }
        })
      },

      setSessionFromSync: (sessionId, data) => {
        if (data.session === null || data.session === undefined) {
          return
        }
        set((state) => {
          const sessions = cloneMap(state.sessions)
          sessions.set(sessionId, {
            session: data.session,
            messages: [...data.messages],
            loadStatus: 'loaded',
            serverSyncStamp: Date.now(),
          })
          const fromMessages = feedbackMapFromMessages(data.messages)
          return {
            sessions,
            feedback: mergeFeedbackMaps(state.feedback, fromMessages),
          }
        })
      },

      setSessionMessages: (sessionId, messages) => {
        set((state) => {
          const sessions = cloneMap(state.sessions)
          const existing = sessions.get(sessionId)
          if (!existing) {
            return state
          }
          sessions.set(sessionId, {
            ...cloneSessionState(existing),
            messages: [...messages],
            serverSyncStamp: Date.now(),
          })
          const fromMessages = feedbackMapFromMessages(messages)
          return {
            sessions,
            feedback: mergeFeedbackMaps(state.feedback, fromMessages),
          }
        })
      },

      upsertSessionMetadata: (session) => {
        set((state) => {
          const sessions = cloneMap(state.sessions)
          const existing = sessions.get(session.id)
          if (existing) {
            sessions.set(session.id, {
              ...cloneSessionState(existing),
              session,
            })
          } else {
            sessions.set(session.id, {
              session,
              messages: [],
              loadStatus: 'loaded',
              serverSyncStamp: Date.now(),
            })
          }
          return { sessions }
        })
      },

      setStreamingStatus: (sessionId, status) => {
        set((state) => {
          const streaming = cloneMap(state.streaming)
          const current = streaming.get(sessionId) ?? defaultStreamingState
          streaming.set(sessionId, {
            ...current,
            status,
            ...(status !== 'error' ? { error: undefined } : {}),
          })
          return { streaming }
        })
      },

      handleStreamError: (sessionId, error) => {
        set((state) => {
          const streaming = cloneMap(state.streaming)
          const current = streaming.get(sessionId) ?? defaultStreamingState
          streaming.set(sessionId, {
            ...current,
            status: 'error',
            error,
            recommendationGatePending: false,
          })
          return { streaming }
        })
      },

      clearStreamError: (sessionId) => {
        set((state) => {
          const streaming = cloneMap(state.streaming)
          const current = streaming.get(sessionId) ?? defaultStreamingState
          streaming.set(sessionId, {
            ...current,
            status: 'idle',
            error: undefined,
          })
          return { streaming }
        })
      },

      setRecommendationGatePending: (sessionId, pending) => {
        set((state) => {
          const streaming = cloneMap(state.streaming)
          const current = streaming.get(sessionId) ?? defaultStreamingState
          streaming.set(sessionId, {
            ...current,
            recommendationGatePending: pending,
          })
          return { streaming }
        })
      },

      clearRecommendationGatePending: (sessionId) => {
        set((state) => {
          const streaming = cloneMap(state.streaming)
          const current = streaming.get(sessionId)
          if (!current) {
            return state
          }
          streaming.set(sessionId, {
            ...current,
            recommendationGatePending: false,
          })
          return { streaming }
        })
      },

      setFeedbackOptimistic: (messageId, feedback) => {
        set((state) => {
          const feedbackMap = cloneMap(state.feedback)
          const existing = feedbackMap.get(messageId)
          feedbackMap.set(messageId, {
            feedback,
            pending: true,
            optimistic: existing?.feedback ?? null,
          })
          return { feedback: feedbackMap }
        })
      },

      setFeedbackPending: (messageId, pending) => {
        set((state) => {
          const feedbackMap = cloneMap(state.feedback)
          const existing = feedbackMap.get(messageId)
          if (!existing) {
            return state
          }
          feedbackMap.set(messageId, { ...existing, pending })
          return { feedback: feedbackMap }
        })
      },

      confirmFeedback: (messageId, feedback) => {
        set((state) => {
          const feedbackMap = cloneMap(state.feedback)
          feedbackMap.set(messageId, { feedback, pending: false })
          return { feedback: feedbackMap }
        })
      },

      rollbackFeedback: (messageId) => {
        set((state) => {
          const feedbackMap = cloneMap(state.feedback)
          const existing = feedbackMap.get(messageId)
          if (!existing) {
            return state
          }
          if (existing.optimistic === undefined) {
            feedbackMap.delete(messageId)
          } else if (existing.optimistic === null) {
            feedbackMap.delete(messageId)
          } else {
            feedbackMap.set(messageId, {
              feedback: existing.optimistic,
              pending: false,
            })
          }
          return { feedback: feedbackMap }
        })
      },

      patchMessageFeedback: (sessionId, messageId, feedback) => {
        set((state) => {
          const sessions = cloneMap(state.sessions)
          const existing = sessions.get(sessionId)
          if (!existing) {
            return state
          }
          const messages = existing.messages.map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  feedbackRating: feedback.rating,
                  feedbackReason: feedback.reason ?? null,
                  feedbackOtherComment: feedback.otherComment ?? null,
                  feedbackSubmittedByUserId: feedback.submittedByUserId,
                  feedbackCreatedAt: feedback.createdAt,
                  feedbackUpdatedAt: feedback.updatedAt,
                }
              : message
          )
          sessions.set(sessionId, {
            ...cloneSessionState(existing),
            messages,
          })
          const feedbackMap = cloneMap(state.feedback)
          feedbackMap.set(messageId, { feedback, pending: false })
          return { sessions, feedback: feedbackMap }
        })
      },

      setBootstrapMessage: (sessionId, message) => {
        set((state) => {
          const bootstrap = cloneMap(state.bootstrap)
          const existing = bootstrap.get(sessionId) ?? {}
          bootstrap.set(sessionId, { ...existing, message })
          return { bootstrap }
        })
      },

      setBootstrapModel: (sessionId, model) => {
        set((state) => {
          const bootstrap = cloneMap(state.bootstrap)
          const existing = bootstrap.get(sessionId) ?? {}
          bootstrap.set(sessionId, { ...existing, model })
          return { bootstrap }
        })
      },

      getAndClearBootstrapMessage: (sessionId) => {
        const entry = get().bootstrap.get(sessionId)
        if (!entry?.message) {
          return null
        }
        const message = entry.message
        set((state) => {
          const bootstrap = cloneMap(state.bootstrap)
          const existing = bootstrap.get(sessionId)
          if (!existing) {
            return state
          }
          const nextEntry = { ...existing }
          delete nextEntry.message
          if (Object.keys(nextEntry).length === 0) {
            bootstrap.delete(sessionId)
          } else {
            bootstrap.set(sessionId, nextEntry)
          }
          return { bootstrap }
        })
        return message
      },

      getAndClearBootstrapModel: (sessionId) => {
        const entry = get().bootstrap.get(sessionId)
        if (!entry?.model) {
          return null
        }
        const model = entry.model
        set((state) => {
          const bootstrap = cloneMap(state.bootstrap)
          const existing = bootstrap.get(sessionId)
          if (!existing) {
            return state
          }
          const nextEntry = { ...existing }
          delete nextEntry.model
          if (Object.keys(nextEntry).length === 0) {
            bootstrap.delete(sessionId)
          } else {
            bootstrap.set(sessionId, nextEntry)
          }
          return { bootstrap }
        })
        return model
      },

      setLandingModel: (model) => {
        set({ landingModel: model })
      },

      getLandingModel: () => get().landingModel,

      setSessionModel: (sessionId, model) => {
        set((state) => {
          const sessionModels = cloneMap(state.sessionModels)
          if (model === undefined) {
            sessionModels.delete(sessionId)
          } else {
            sessionModels.set(sessionId, model)
          }
          return { sessionModels }
        })
      },

      getSessionModel: (sessionId) => get().sessionModels.get(sessionId),

      setProductRecommendations: (productId, sessionId, recommendations) => {
        const key = productRecommendationsKey(productId, sessionId)
        set((state) => {
          const productRecommendations = cloneMap(state.productRecommendations)
          const existing = productRecommendations.get(key) ?? []
          const existingIds = new Set(existing.map((rec) => rec.id))
          const newOnes = recommendations.filter((rec) => !existingIds.has(rec.id))
          if (newOnes.length === 0 && existing.length > 0) {
            return state
          }
          productRecommendations.set(key, [...existing, ...newOnes])
          return { productRecommendations }
        })
      },

      getProductRecommendations: (productId, sessionId) => {
        const key = productRecommendationsKey(productId, sessionId)
        return get().productRecommendations.get(key)
      },
    }),
    {
      name: 'llm-chat-store',
      partialize: (state) => ({
        bootstrap: state.bootstrap,
        landingModel: state.landingModel,
      }),
      storage: {
        getItem: (name) => {
          const raw = sessionStorage.getItem(name)
          if (!raw) {
            return null
          }
          try {
            const parsed = JSON.parse(raw) as {
              state: {
                bootstrap?: Array<[string, BootstrapEntry]>
                landingModel?: string
              }
              version: number
            }
            const bootstrapEntries = parsed.state.bootstrap ?? []
            return {
              state: {
                bootstrap: new Map(bootstrapEntries),
                landingModel: parsed.state.landingModel,
              },
              version: parsed.version,
            }
          } catch {
            return null
          }
        },
        setItem: (name, value) => {
          const bootstrap = value.state.bootstrap as Map<string, BootstrapEntry>
          sessionStorage.setItem(
            name,
            JSON.stringify({
              state: {
                bootstrap: Array.from(bootstrap.entries()),
                landingModel: value.state.landingModel,
              },
              version: value.version,
            })
          )
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name)
        },
      },
    }
  )
)

export function selectSessionState(
  state: LlmChatStore,
  sessionId: string | undefined
): SessionState | undefined {
  if (!sessionId) {
    return undefined
  }
  return state.sessions.get(sessionId)
}

export function selectSession(
  state: LlmChatStore,
  sessionId: string | undefined
): LlmChatSessionDto | null | undefined {
  const sessionState = selectSessionState(state, sessionId)
  if (sessionState === undefined) {
    return undefined
  }
  return sessionState.session
}

export function selectSessionMessages(
  state: LlmChatStore,
  sessionId: string | undefined
): LlmChatMessageDto[] {
  if (!sessionId) {
    return []
  }
  return state.sessions.get(sessionId)?.messages ?? []
}

export function selectMessageFeedback(
  state: LlmChatStore,
  messageId: string
): LlmChatMessageFeedbackDto | undefined {
  const entry = state.feedback.get(messageId)
  if (!entry) {
    return undefined
  }
  return entry.feedback ?? undefined
}

export function selectFeedbackPending(
  state: LlmChatStore,
  messageId: string
): boolean {
  return state.feedback.get(messageId)?.pending ?? false
}

export async function submitFeedbackViaStore(input: {
  sessionId: string
  messageId: string
  payload: SubmitLlmChatMessageFeedbackPayload
  submitApi: typeof submitLlmChatMessageFeedbackApi
}): Promise<void> {
  const store = useLlmChatStore.getState()
  const optimisticFeedback: LlmChatMessageFeedbackDto = {
    rating: input.payload.rating,
    ...(input.payload.rating === 'down' && {
      reason: input.payload.reason,
      ...(input.payload.otherComment !== undefined && {
        otherComment: input.payload.otherComment,
      }),
    }),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    submittedByUserId: '',
  }
  store.setFeedbackOptimistic(input.messageId, optimisticFeedback)
  try {
    const response = await input.submitApi({
      sessionId: input.sessionId,
      messageId: input.messageId,
      payload: input.payload,
    })
    store.patchMessageFeedback(
      input.sessionId,
      response.messageId,
      response.feedback
    )
  } catch (error) {
    store.rollbackFeedback(input.messageId)
    throw error
  }
}
